import React, { createContext, useContext } from 'react';
import type { TreeSelectionStore } from './treeSelectionStore';

export interface TreeContextValue {
  /**
   * Selection / highlight state lives in its own per-Explorer zustand store
   * rather than on this context value, so a selection change re-renders only
   * the two rows whose selected-ness actually flipped instead of every row in
   * the tree. See `treeSelectionStore.ts` for the full rationale — this value
   * object must stay referentially stable across selection changes for that
   * to hold.
   */
  selectionStore: TreeSelectionStore;
  onNodeExpand: (nodeId: string) => void;
  onNodeSelect: (nodeId: string) => void;
  onNodeRefresh: (nodeId: string) => void;
  onContextMenuAction: (nodeId: string, actionId: string) => void;
  onNodeHighlightComplete?: (connectionId: string) => void;
  onHighlightedNodeComplete?: () => void;
  onReconnect?: (connectionId: string) => Promise<boolean>;
  iconMap: Record<string, React.ElementType>;
  /**
   * T12 fix round 1, M-4 — AI-launcher CLI-gating state, read once by
   * `Explorer.tsx` (via `useCliDetection()` upstream in `DatabaseExplorer.tsx`)
   * and provided here so every `TreeNode` row's "AI" submenu reads it from
   * context instead of each row calling the detection hook itself (which was
   * one IPC probe + one permanent listener per row on a cold cache).
   */
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
}

export const TreeContext = createContext<TreeContextValue | null>(null);

export function useTreeContext(): TreeContextValue {
  const context = useContext(TreeContext);
  if (!context) {
    throw new Error('useTreeContext must be used within TreeProvider');
  }
  return context;
}

export function TreeProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: TreeContextValue;
}) {
  return <TreeContext.Provider value={value}>{children}</TreeContext.Provider>;
}
